import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useSEO } from "../lib/useSEO";
import { SEO } from "../lib/seoConfig";
import { Calendar, ArrowLeft } from "lucide-react";
import { SITE } from "../lib/site";

// Shared legal page wrapper — clean reading layout for long-form prose.
const LegalLayout = ({ path, children, lastUpdated }) => {
  const seo = SEO[path] || {};
  useSEO({ ...seo, path });
  return (
    <Layout>
      <article className="container-soft max-w-3xl py-16 sm:py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-forest font-semibold hover:underline mb-8" data-testid="legal-back-home">
          <ArrowLeft className="w-5 h-5" /> Back to home
        </Link>
        <div className="prose-daisy">{children}</div>
        {lastUpdated && (
          <p className="mt-10 text-base text-forest/70">Last updated: {lastUpdated}</p>
        )}
        <div className="mt-12 soft-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 bg-sage/25">
          <p className="text-lg text-forest flex-1">Any questions? Sarah is happy to help.</p>
          <Link to="/contact#booking" className="btn-primary h-12 px-5 text-base" data-testid="legal-book">
            <Calendar className="w-5 h-5" /> Book a Free Call
          </Link>
        </div>
      </article>
    </Layout>
  );
};

const H1 = ({ children }) => <h1 className="font-serif text-4xl sm:text-5xl text-forest leading-tight">{children}</h1>;
const H2 = ({ children }) => <h2 className="font-serif text-2xl sm:text-3xl text-forest mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="mt-4 text-lg text-charcoal/90 leading-relaxed">{children}</p>;
const UL = ({ children }) => <ul className="mt-4 space-y-2 list-disc pl-6 text-lg text-charcoal/90">{children}</ul>;

export const PrivacyPolicy = () => (
  <LegalLayout path="/privacy" lastUpdated="May 2026">
    <H1>Privacy Policy</H1>
    <P>
      Daisy Does ("we", "us", "our") takes your privacy seriously. This plain-English policy explains what personal information
      we collect, why we collect it, how we use it and the rights you have under UK data protection law (UK GDPR and the Data
      Protection Act 2018).
    </P>

    <H2>Who we are</H2>
    <P>
      Daisy Does is run by Sarah and provides practical administrative support. You can reach us on{" "}
      <a className="text-forest underline" href={SITE.tel}>{SITE.phoneDisplay}</a> or by email at{" "}
      <a className="text-forest underline" href={`mailto:${SITE.email}`}>{SITE.email}</a>.
    </P>

    <H2>What we collect</H2>
    <UL>
      <li>Your name, phone number and email address (when you book a call or send an enquiry).</li>
      <li>What you have asked us to help with, and whether the support is for you or someone else.</li>
      <li>Any accessibility or communication needs you choose to tell us about.</li>
      <li>Date and time of your booking and basic technical information such as your browser type and approximate location (via standard server logs).</li>
    </UL>

    <H2>Why we collect it</H2>
    <UL>
      <li>To contact you about the call or enquiry you have submitted.</li>
      <li>To deliver the practical admin support you have asked for.</li>
      <li>To keep simple records of work done so we can answer your questions later.</li>
      <li>To meet our legal and tax obligations.</li>
    </UL>

    <H2>Our lawful basis</H2>
    <P>
      We rely on: <strong>your consent</strong> (when you submit a booking or enquiry); <strong>contract</strong> (where you ask us
      to carry out work for you); and <strong>legitimate interests</strong> (to keep simple records of our service).
    </P>

    <H2>Sensitive information</H2>
    <P>
      Some of the work you ask us to help with — for example benefit applications — may involve health or financial details.
      We only ask for what is needed to help with the specific task, we keep it for as long as we need it, and we delete it
      afterwards unless you ask us to keep it for follow-up work.
    </P>

    <H2>Who we share information with</H2>
    <UL>
      <li>The organisations you ask us to deal with on your behalf (for example, a local authority or a benefits office).</li>
      <li>Trusted service providers used to run this website and our email (for example, hosting and email providers).</li>
      <li>We will <strong>never</strong> sell your information.</li>
    </UL>

    <H2>How long we keep it</H2>
    <P>
      We keep booking and enquiry details for up to 24 months unless we are working with you on something longer or we are
      legally required to keep it longer. You can ask us to delete your data at any time.
    </P>

    <H2>Your rights</H2>
    <UL>
      <li>Ask for a copy of the information we hold about you.</li>
      <li>Ask us to correct anything that is wrong.</li>
      <li>Ask us to delete your information.</li>
      <li>Withdraw your consent at any time.</li>
      <li>Complain to the UK Information Commissioner's Office (<a className="text-forest underline" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>).</li>
    </UL>

    <H2>Contact</H2>
    <P>
      For any privacy questions, please contact Sarah on {SITE.phoneDisplay} or at {SITE.email}.
    </P>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout path="/terms" lastUpdated="May 2026">
    <H1>Terms of Service</H1>
    <P>
      These plain-English terms apply when you use Daisy Does services. By booking a call or asking us to help, you agree to
      these terms.
    </P>

    <H2>What we do</H2>
    <P>
      Daisy Does provides practical administrative support: help with forms, applications, household admin, online accounts,
      letters and everyday tasks. We work for you — alongside you — and explain everything in plain English.
    </P>

    <H2>What we do not do</H2>
    <UL>
      <li>We are <strong>not</strong> regulated benefits, legal, financial or tax advisers.</li>
      <li>We <strong>cannot</strong> guarantee benefit awards, eligibility decisions or application outcomes.</li>
      <li>We <strong>are not</strong> a care agency or medical service.</li>
      <li>Where formal advice is needed, we will signpost you to an appropriate qualified adviser.</li>
    </UL>

    <H2>Bookings and the free call</H2>
    <UL>
      <li>The free 15-minute introductory call is free and carries no obligation.</li>
      <li>If we then agree on work, we will confirm the scope and price before we start.</li>
      <li>You can reschedule a booked call by contacting Sarah as soon as you can.</li>
    </UL>

    <H2>Pricing and payment</H2>
    <UL>
      <li>Hourly support is charged at £45 per hour unless otherwise agreed.</li>
      <li>Application packages and home admin sessions are quoted as a fixed fee in advance.</li>
      <li>Payment is due on invoice unless we have agreed otherwise.</li>
    </UL>

    <H2>Your responsibilities</H2>
    <UL>
      <li>Provide accurate information to the best of your knowledge.</li>
      <li>Let us know promptly if anything changes.</li>
      <li>Make any final decisions on applications — we help, but the decisions are yours.</li>
    </UL>

    <H2>Cancellation</H2>
    <P>
      Either side can stop working together at any time. If work has already been done, we will fairly bill for time spent.
    </P>

    <H2>Liability</H2>
    <P>
      We carry out our work with reasonable care and skill. We are not liable for outcomes that depend on third parties (for
      example, benefit award decisions), and our total liability is limited to the fees you have paid us for the work in
      question.
    </P>

    <H2>Governing law</H2>
    <P>
      These terms are governed by the law of England and Wales, and any dispute will be subject to the exclusive jurisdiction
      of the courts of England and Wales.
    </P>
  </LegalLayout>
);

export const CookiePolicy = () => (
  <LegalLayout path="/cookies" lastUpdated="May 2026">
    <H1>Cookie Policy</H1>
    <P>
      This site keeps things simple. We only use cookies and similar storage that are <strong>strictly necessary</strong> to
      make the website work, plus a small amount of basic analytics if and when we add it.
    </P>

    <H2>What is a cookie?</H2>
    <P>
      A cookie is a small text file stored by your browser when you visit a website. Cookies help websites remember things
      like your preferences and whether you are signed in.
    </P>

    <H2>What we use</H2>
    <UL>
      <li><strong>Essential</strong> — used to keep the site working and to remember small interface preferences. These do not require consent.</li>
      <li><strong>Analytics</strong> — if and when we add an analytics tool, it will only be loaded with your consent and will be used to understand which pages are most useful.</li>
      <li><strong>Marketing</strong> — we do not currently use marketing or advertising cookies.</li>
    </UL>

    <H2>Managing cookies</H2>
    <P>
      You can control cookies through your browser settings. Most browsers let you block or delete cookies — please refer to
      your browser's help pages for instructions.
    </P>

    <H2>Changes to this policy</H2>
    <P>
      If we make significant changes, we will update this page and the "last updated" date below.
    </P>
  </LegalLayout>
);
